import React, { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Slide from '@mui/material/Slide';

// Snackbar Context
const SnackbarContext = createContext();

// Slide transition for snackbar
function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

/**
 * Global Snackbar Provider
 * Wrap your app with this provider to enable snackbar notifications throughout
 * 
 * Usage in App.js:
 * <SnackbarProvider>
 *   <YourApp />
 * </SnackbarProvider>
 */
export function SnackbarProvider({ children }) {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info', // 'success', 'error', 'warning', 'info'
    title: null,
    duration: 6000,
  });

  /**
   * Show snackbar notification
   * @param {string} message - Message to display
   * @param {string} severity - 'success', 'error', 'warning', 'info'
   * @param {string} title - Optional title
   * @param {number} duration - Duration in milliseconds (default: 6000)
   */
  const showSnackbar = useCallback((message, severity = 'info', title = null, duration = 6000) => {
    setSnackbar({
      open: true,
      message,
      severity,
      title,
      duration,
    });
  }, []);

  /**
   * Show success notification
   */
  const showSuccess = useCallback((message, title = 'Berjaya') => {
    showSnackbar(message, 'success', title);
  }, [showSnackbar]);

  /**
   * Show error notification
   */
  const showError = useCallback((message, title = 'Ralat') => {
    showSnackbar(message, 'error', title, 8000); // Longer duration for errors
  }, [showSnackbar]);

  /**
   * Show warning notification
   */
  const showWarning = useCallback((message, title = 'Amaran') => {
    showSnackbar(message, 'warning', title);
  }, [showSnackbar]);

  /**
   * Show info notification
   */
  const showInfo = useCallback((message, title = null) => {
    showSnackbar(message, 'info', title);
  }, [showSnackbar]);

  /**
   * Close snackbar
   */
  const closeSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const handleClose = (event, reason) => {
    // Don't close on clickaway (only on close button or timeout)
    if (reason === 'clickaway') {
      return;
    }
    closeSnackbar();
  };

  return (
    <SnackbarContext.Provider
      value={{
        showSnackbar,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        closeSnackbar,
      }}
    >
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.duration}
        onClose={handleClose}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          elevation={6}
          sx={{
            width: '100%',
            minWidth: '300px',
            maxWidth: '500px',
            '& .MuiAlert-icon': {
              display: snackbar.title ? 'flex' : 'none', // Hide icon when title is shown
            },
          }}
          iconMapping={{
            error: <span style={{ fontSize: '0px' }}>Ralat</span>,
            success: <span style={{ fontSize: '0px' }}>Berjaya</span>,
            warning: <span style={{ fontSize: '0px' }}>Amaran</span>,
            info: <span style={{ fontSize: '0px' }}>Maklumat</span>,
          }}
        >
          {snackbar.title && <AlertTitle>{snackbar.title}</AlertTitle>}
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

/**
 * Hook to access snackbar functions
 * 
 * Usage in components:
 * const { showSuccess, showError, showWarning, showInfo } = useSnackbar();
 * 
 * Examples:
 * showSuccess('Data berjaya disimpan!');
 * showError('Gagal menyimpan data', 'Ralat');
 * showWarning('Sila isi semua maklumat wajib');
 * showInfo('Proses sedang dijalankan...');
 */
export function useSnackbar() {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
}

SnackbarProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SnackbarProvider;
